import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useLogic } from '~common/ui-logic/hooks/logic'
import { useAppContext } from '~/app-context'
import {
    type CategorySelectLogicDependencies,
    CategorySelectLogic,
} from './logic'
import { TextField } from '../../../../firebase-functions/src/common/components/text-field'
import { FloatingPopout } from '~common/components/floating-popout'
import { Icon } from '~common/components/icon'

import { Button } from '~common/components/button'
import { useTheme } from '~common/theme/theme-provider'
import { getTransparentHex } from '~common/utils/getTransparentHex'
import { resolveCategoryLabel } from '~/features/transactions/services/categories'

export function CategorySelect(props: CategorySelectLogicDependencies) {
    const appContext = useAppContext()
    const { logic, state } = useLogic(CategorySelectLogic, {
        ...props,
        ...appContext,
    })
    const theme = useTheme()
    const isDev = import.meta.env.MODE === 'development'

    const filteredCategories = Object.values(logic.getFilteredCategories())

    useEffect(() => {
        if (!state.isOpen) return

        const handleKeyDown = (e: KeyboardEvent) => {
            if (
                e.key === 'ArrowDown' ||
                e.key === 'ArrowUp' ||
                (e.key === 'Enter' && state.isOpen)
            ) {
                e.preventDefault()
                logic.handleKeyDown(e.key, state.textValue)
            }
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [state.isOpen, state.textValue, logic, props.initValue])

    const handleTutorialClick = () => {
        logic.toggleTutorial()
    }

    const handleSettingsClick = () => {
        appContext.services.globalEvents.emit({
            type: 'openModal',
            modalType: 'settings',
            metadata: {
                openSection: 'booking-categories',
            },
        })
        logic.closePopout()
    }

    const transactionData = props.transactionId
        ? appContext.services.cache.getKey('transactions')?.[
              props.transactionId
          ]
        : undefined

    const transactionType =
        transactionData && transactionData?.amount < 0 ? 'EXPENSE' : 'REVENUE'
    const textColor =
        transactionType === 'EXPENSE'
            ? theme.theme.colors.error
            : transactionType === 'REVENUE'
              ? theme.theme.colors.info
              : undefined

    return (
        <CategorySelectContainer width={props.width}>
            <FloatingPopout
                open={state.isOpen}
                onOpenChange={logic.setIsOpen}
                placement="bottom-start"
                offset={4}
                trigger={
                    <TextField
                        placeholder={props.placeholder}
                        editable={true}
                        onChange={logic.handleInputChange}
                        initActive={true}
                        controlledValue={state.textValue}
                        onConfirm={(value) => {
                            logic.handleKeyDown(
                                'Enter',
                                value || state.textValue,
                            )
                        }}
                        onClick={() => {
                            if (!state.isOpen) {
                                logic.setIsOpen(true)
                            }
                        }}
                        icon={
                            state.selectedCategoryId && transactionData ? (
                                <Icon
                                    size="small"
                                    name={
                                        transactionData.amount > 0
                                            ? 'arrow-down'
                                            : 'arrow-up'
                                    }
                                    iconColor={
                                        transactionData.amount > 0
                                            ? theme.theme.colors.info
                                            : theme.theme.colors.error
                                    }
                                />
                            ) : undefined
                        }
                        variant={props.variant}
                        disableOnBlur={props.disableOnBlur}
                        onSort={props.onSortClick}
                        onClear={(shiftKey) => logic.handleClear(shiftKey)}
                        sortDirection={props.sortDirection}
                        size={props.size || 'medium'}
                        activated={
                            props.variant === 'tableHeader' &&
                            state.textValue.length > 0
                        }
                        resizable={props.resizable}
                        textColor={
                            textColor
                                ? getTransparentHex(textColor, 90)
                                : undefined
                        }
                        actionButtonsOnlyOnHover={true}
                        actionButtons={
                            props.transactionId
                                ? [
                                      <Button
                                          icon="reload"
                                          key="reload"
                                          onClick={(e) => {
                                              e.stopPropagation()
                                              logic.handleReload()
                                          }}
                                          variant="ghost"
                                          tooltip="Refresh category match"
                                          size="small"
                                      />,
                                  ]
                                : undefined
                        }
                    />
                }
                content={
                    <CategoryListContent>
                        {state.showTutorial ? (
                            <TutorialContent>
                                <TutorialTitle>
                                    Category Selector Tutorial
                                </TutorialTitle>
                                <TutorialSection>
                                    <TutorialSectionTitle>
                                        Keyboard Navigation:
                                    </TutorialSectionTitle>
                                    <TutorialList>
                                        <TutorialListItem>
                                            Use <strong>Arrow Up</strong> and{' '}
                                            <strong>Arrow Down</strong> to
                                            navigate through categories
                                        </TutorialListItem>
                                        <TutorialListItem>
                                            Press <strong>Enter</strong> to
                                            select the highlighted category
                                        </TutorialListItem>
                                        <TutorialListItem>
                                            Type to filter categories by name or
                                            description
                                        </TutorialListItem>
                                    </TutorialList>
                                </TutorialSection>
                                <TutorialSection>
                                    <TutorialSectionTitle>
                                        Tips:
                                    </TutorialSectionTitle>
                                    <TutorialList>
                                        <TutorialListItem>
                                            Categories are color-coded: expenses
                                            are red, income is blue
                                        </TutorialListItem>
                                        <TutorialListItem>
                                            Click on a category or use keyboard
                                            navigation to select
                                        </TutorialListItem>
                                        <TutorialListItem>
                                            Use the Settings button to configure
                                            category preferences
                                        </TutorialListItem>
                                    </TutorialList>
                                </TutorialSection>
                            </TutorialContent>
                        ) : (
                            <>
                                <CategoryList>
                                    {filteredCategories.length === 0 ? (
                                        <NoResults>
                                            No categories found
                                        </NoResults>
                                    ) : (
                                        filteredCategories.map(
                                            (category, index) => {
                                                const isSelected =
                                                    state.selectedCategoryId ===
                                                    Number(category.id)
                                                const isHighlighted =
                                                    state.isOpen &&
                                                    index ===
                                                        state.highlightedIndex
                                                return (
                                                    <CategoryItem
                                                        key={category.id}
                                                        onClick={(e) => {
                                                            e.stopPropagation()
                                                            logic.handleCategorySelect(
                                                                Number(
                                                                    category.id,
                                                                ),
                                                            )
                                                        }}
                                                        $isSelected={isSelected}
                                                        $isHighlighted={
                                                            isHighlighted
                                                        }
                                                        ref={(el) => {
                                                            if (
                                                                isHighlighted &&
                                                                el
                                                            ) {
                                                                el.scrollIntoView(
                                                                    {
                                                                        block: 'nearest',
                                                                    },
                                                                )
                                                            }
                                                        }}
                                                    >
                                                        <CategoryId>
                                                            {category.id}
                                                        </CategoryId>
                                                        <CategoryContent>
                                                            <CategoryLabel>
                                                                {category.label}
                                                            </CategoryLabel>
                                                            {category.description && (
                                                                <CategoryDescription>
                                                                    {
                                                                        category.description
                                                                    }
                                                                </CategoryDescription>
                                                            )}
                                                        </CategoryContent>
                                                    </CategoryItem>
                                                )
                                            },
                                        )
                                    )}
                                </CategoryList>
                            </>
                        )}
                        <CategoryFooter>
                            <CategoryFooterLeft>
                                <Button
                                    label={
                                        state.showTutorial ? 'Back' : 'Tutorial'
                                    }
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleTutorialClick()
                                    }}
                                    variant="ghost"
                                    size="small"
                                    icon="help"
                                />
                                <Button
                                    label="Settings"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        handleSettingsClick()
                                    }}
                                    variant="ghost"
                                    size="small"
                                    icon="gear"
                                />
                            </CategoryFooterLeft>
                            {/* <Button
                                label={'Categorize All'}
                                tooltip="Auto-Categorize all \nmatched transactions"
                                onClick={(e) => {
                                    e.stopPropagation()
                                }}
                                variant="ghost"
                                size="small"
                                icon="magic"
                            /> */}
                            {isDev && (
                                <CategoryFooterRight>
                                    <Button
                                        label="Categorize All"
                                        tooltip="Auto-categorize all uncategorized expenses using LLM"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            logic.categorizeAllUncategorizedExpenses()
                                        }}
                                        variant="ghost"
                                        size="small"
                                        icon="magic"
                                    />
                                </CategoryFooterRight>
                            )}
                            {isDev && (
                                <CategoryFooterRight>
                                    <Button
                                        label="Clear Categories"
                                        tooltip="Auto-categorize all uncategorized expenses using LLM"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            logic.removeAllCategories()
                                        }}
                                        variant="ghost"
                                        size="small"
                                        icon="magic"
                                    />
                                </CategoryFooterRight>
                            )}
                        </CategoryFooter>
                    </CategoryListContent>
                }
            />
        </CategorySelectContainer>
    )
}

const CategorySelectContainer = styled.div<{ width?: string }>`
    position: relative;
    width: ${({ width }) => width ?? '100%'};
`

const CategoryListContent = styled.div`
    display: flex;
    flex-direction: column;
    max-height: 400px;
    overflow-y: auto;
    /* padding: ${({ theme }) => theme.spacing[2]}; */
    min-width: 300px;
`

const CategoryList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[1]};
    padding-bottom: ${({ theme }) => theme.spacing[10]};
`

const CategoryItem = styled.div<{
    $isSelected: boolean
    $isHighlighted: boolean
}>`
    display: flex;
    flex-direction: row;
    gap: ${({ theme }) => theme.spacing[3]};
    padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    cursor: pointer;
    background-color: ${({ theme }) => theme.colors.greyScale[400]};
    transition: background-color 0.15s ease;

    &:hover {
        background-color: ${({ theme }) => theme.colors.greyScale[300]};
    }

    ${({ theme, $isSelected, $isHighlighted }) =>
        $isSelected
            ? `background-color: ${theme.colors.greyScale[400]};`
            : $isHighlighted
              ? `background-color: ${theme.colors.greyScale[300]};`
              : ''}

    ${({ theme, $isSelected, $isHighlighted }) =>
        ($isSelected || $isHighlighted) &&
        `&:hover {
            background-color: ${theme.colors.greyScale[300]};
        }`}
    
    max-width: 400px;
`

const CategoryId = styled.div`
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
    flex-shrink: 0;
`

const CategoryContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[0.5]};
    flex: 1;
`

const CategoryLabel = styled.div`
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
    color: ${({ theme }) => theme.colors.text.primary};
`

const CategoryDescription = styled.div`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-weight: ${({ theme }) => theme.typography.fontWeight.normal};
`

const NoResults = styled.div`
    padding: ${({ theme }) => theme.spacing[4]};
    text-align: center;
    color: ${({ theme }) => theme.colors.text.secondary};
`

const CategoryFooter = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[2]};
    justify-content: flex-end;
    position: fixed;
    width: fill-available;
    flex: 1;
    width: 100%;
    bottom: 0;
    left: 0;
    background-color: ${({ theme }) =>
        getTransparentHex(theme.colors.background.bg1, 50)};
    backdrop-filter: blur(10px);
    padding: ${({ theme }) => theme.spacing[2]};
    box-sizing: border-box;
    //prettier-ignore
    border-radius: 0 0 ${({ theme }) => theme.borderRadius.lg} ${({ theme }) =>
        theme.borderRadius.lg};
`

const ReloadButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
    padding: ${({ theme }) => theme.spacing[1]};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    color: ${({ theme }) => theme.colors.text.secondary};

    &:hover {
        background-color: ${({ theme }) => theme.colors.greyScale[200]};
        color: ${({ theme }) => theme.colors.text.primary};
    }
`

const TutorialContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[4]};
    padding: ${({ theme }) => theme.spacing[2]};
`

const TutorialTitle = styled.h3`
    font-size: ${({ theme }) => theme.typography.fontSize.xl};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0;
`

const TutorialSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
`

const TutorialSectionTitle = styled.p`
    font-size: ${({ theme }) => theme.typography.fontSize.base};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    color: ${({ theme }) => theme.colors.text.primary};
    margin: 0;
`

const TutorialList = styled.ul`
    margin: 0;
    padding-left: ${({ theme }) => theme.spacing[5]};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing[2]};
`

const TutorialListItem = styled.li`
    font-size: ${({ theme }) => theme.typography.fontSize.sm};
    color: ${({ theme }) => theme.colors.text.secondary};
    line-height: 1.5;
`

const CategoryFooterLeft = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[2]};
    justify-content: flex-start;
`

const CategoryFooterRight = styled.div`
    display: flex;
    gap: ${({ theme }) => theme.spacing[2]};
    justify-content: flex-end;
`
